import React, { useEffect, useState } from 'react'
import { useSocket } from '../../contexts/SocketContext'
import { IconBell, crmToast } from './crmUi'
import { getAlerts, markAlertRead, markAllAlertsRead, type CrmAlert } from './crmApi'

/**
 * The alerts bell (Phase 2.6).
 *
 * "Alert the team" used to write a line into a deal's activity feed. Nobody sees that — the
 * whole reason you're alerting someone is that they aren't looking at that lead. Alerts now
 * arrive here: live over the socket while you're on the page, and persisted, so one fired
 * while you were at lunch is still waiting when you get back.
 *
 * Deliberately CRM-only: it lives in the CRM board header rather than the shared dashboard
 * chrome, so nothing about the support side's notification behaviour changes.
 */

const SEV: Record<string, { bg: string; fg: string; dot: string }> = {
  info: { bg: '#EFF6FF', fg: '#1E40AF', dot: '#3B82F6' },
  warn: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  critical: { bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444' },
}

const ago = (iso: string) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (d < 1) return 'just now'
  if (d < 60) return `${d}m ago`
  const h = Math.floor(d / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

export const CrmAlertsBell: React.FC<{ onOpenDeal?: (dealId: string) => void }> = ({ onOpenDeal }) => {
  const { socket } = useSocket()
  const [alerts, setAlerts] = useState<CrmAlert[]>([])
  const [unread, setUnread] = useState(0)
  const [open, setOpen] = useState(false)

  const load = () => {
    getAlerts()
      .then((r) => { setAlerts(r.alerts); setUnread(r.unread) })
      .catch(() => { /* alerts unavailable — the bell just stays quiet */ })
  }

  useEffect(load, [])

  // Live: an alert raised while you're looking at the board lands immediately.
  useEffect(() => {
    if (!socket) return
    const onAlert = (a: CrmAlert) => {
      setAlerts((prev) => [a, ...prev].slice(0, 50))
      setUnread((n) => n + 1)
      crmToast(`${a.title}${a.dealName ? ` — ${a.dealName}` : ''}`, a.severity === 'critical' ? 'error' : 'success')
    }
    socket.on('crm_alert', onAlert)
    return () => { socket.off('crm_alert', onAlert) }
  }, [socket])

  const readOne = (id: string) => {
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)))
    setUnread((n) => Math.max(0, n - 1))
    markAlertRead(id).catch(() => {})
  }
  const readAll = () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })))
    setUnread(0)
    markAllAlertsRead().catch(() => {})
  }

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); if (!open) load() }}
        className="relative w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-surface-hover transition-colors"
        title="CRM alerts"
      >
        <IconBell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
            style={{ background: '#EF4444' }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[380px] max-h-[420px] overflow-auto rounded-xl border border-border bg-surface shadow-lg z-50">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border sticky top-0 bg-surface">
              <p className="text-sm font-semibold text-txt-primary">Alerts</p>
              {unread > 0 && (
                <button onClick={readAll} className="text-[11px] text-brand-600 hover:underline">Mark all read</button>
              )}
            </div>

            {alerts.length === 0 ? (
              <div className="px-4 py-6">
                <p className="text-xs text-txt-secondary">No alerts.</p>
                <p className="text-[11px] text-txt-tertiary mt-1 leading-relaxed">
                  Alerts arrive here from automations and sequences. Until now, an “alert the team” action
                  only wrote a note into a lead’s timeline — which is why you have never seen one.
                </p>
              </div>
            ) : (
              alerts.map((a) => {
                const s = SEV[a.severity] || SEV.info
                return (
                  <button
                    key={a.id}
                    onClick={() => { readOne(a.id); if (a.dealId && onOpenDeal) { onOpenDeal(a.dealId); setOpen(false) } }}
                    className={`w-full text-left px-4 py-2.5 border-b border-border last:border-0 hover:bg-surface-hover transition-colors ${a.read ? 'opacity-60' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: s.dot }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-medium text-txt-primary truncate">{a.title}</p>
                        {a.dealName && <p className="text-[11px] text-txt-secondary truncate">{a.dealName}</p>}
                        <p className="text-[10px] text-txt-tertiary mt-0.5">{a.source} · {ago(a.createdAt)}</p>
                      </div>
                      {!a.read && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0" style={{ background: s.bg, color: s.fg }}>NEW</span>}
                    </div>
                  </button>
                )
              })
            )}
          </div>
        </>
      )}
    </div>
  )
}
